import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getMyMessageConversation,
  listMessageConversations,
  listMessagesWith,
  markConversationRead,
  openMessagesStream,
  sendMessageTo,
} from "../lib/api";
import { readProfilePhotoUrl, resolveProfilePhotoSrc } from "../lib/profilePhoto";
import { useAuth } from "../modules/auth/AuthContext";
import type { ChatMessage, CoachConversationSummary } from "../types";

type FilterKey = "Todos" | "No leidos";

interface Conversation {
  id: string;
  clientId: string;
  title: string;
  subtitle: string;
  unread: number;
  lastMessage: ChatMessage | null;
  hasPortalAccount: boolean;
  profilePhotoUrl?: string | null;
}

interface MessagesViewProps {
  mode: "coach" | "client";
}

const EMOJIS = [
  "💪", "🏋️", "🏃", "🤸", "🥇", "🏆",
  "🔥", "⚡", "🎯", "✅", "💯", "⏱️",
  "🥗", "🥦", "🍎", "💧", "🍳", "🥑",
  "👏", "🙌", "👍", "👌", "🤝", "🙏",
  "❤️", "😍", "😊", "😂", "😅", "🥵",
];

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "JC";
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? "").join("") || "JC";
}

function formatTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return date.toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit" });
  }
  return date.toLocaleDateString("es-PE", { day: "numeric", month: "short" });
}

function mineFor(mode: "coach" | "client", message: ChatMessage) {
  if (mode === "coach") return message.senderRole === "COACH" || message.senderRole === "ADMIN";
  return message.senderRole === "CLIENT";
}

export function MessagesView({ mode }: MessagesViewProps) {
  const { token, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterKey>("Todos");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  // Mobile master/detail: show the conversation list until a chat is opened
  const [mobileView, setMobileView] = useState<"list" | "chat">(mode === "coach" ? "list" : "chat");
  const chatBodyRef = useRef<HTMLDivElement | null>(null);
  const emojiWrapRef = useRef<HTMLDivElement | null>(null);

  const selected = conversations.find((conversation) => conversation.id === selectedId) ?? null;

  useEffect(() => {
    setSearch(searchParams.get("q") ?? "");
    if (searchParams.get("q")) setMobileView("list");
  }, [searchParams]);

  async function refreshConversations(preserveSelected = true) {
    setLoadingThreads(true);
    try {
      if (mode === "coach") {
        const data = await listMessageConversations();
        const next: Conversation[] = data.map((summary: CoachConversationSummary) => ({
          id: summary.clientId,
          clientId: summary.clientId,
          title: `${summary.firstName} ${summary.lastName}`.trim() || "Cliente",
          subtitle: summary.lastMessage?.content || summary.objective || "Sin mensajes todavia.",
          unread: summary.unread,
          lastMessage: summary.lastMessage,
          hasPortalAccount: summary.hasPortalAccount,
          profilePhotoUrl: readProfilePhotoUrl(summary),
        }));
        setConversations(next);
        if (!preserveSelected || !next.some((conv) => conv.id === selectedId)) {
          const first = next.find((conv) => conv.hasPortalAccount) ?? next[0];
          setSelectedId(first?.id ?? "");
        }
      } else {
        const data = await getMyMessageConversation();
        const conversation: Conversation = {
          id: data.clientId,
          clientId: data.clientId,
          title: `${data.coach.firstName} ${data.coach.lastName}`.trim() || "Coach",
          subtitle: data.lastMessage?.content || "Sin mensajes todavia.",
          unread: data.unread,
          lastMessage: data.lastMessage,
          hasPortalAccount: true,
          profilePhotoUrl: readProfilePhotoUrl(data.coach),
        };
        setConversations([conversation]);
        setSelectedId(conversation.id);
      }
      setError(null);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "No se pudo cargar la mensajeria.";
      setError(typeof message === "string" ? message : "No se pudo cargar la mensajeria.");
      setConversations([]);
      setSelectedId("");
    } finally {
      setLoadingThreads(false);
    }
  }

  useEffect(() => {
    refreshConversations(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return;
    }

    let cancelled = false;
    setLoadingMessages(true);
    listMessagesWith(selectedId)
      .then((data) => {
        if (cancelled) return;
        setMessages(data);
        const lastMessage = data[data.length - 1] ?? null;
        if (lastMessage) {
          setConversations((current) =>
            current.map((conversation) =>
              conversation.id === selectedId
                ? {
                    ...conversation,
                    lastMessage,
                    subtitle: lastMessage.content,
                  }
                : conversation,
            ),
          );
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err?.response?.data?.message ||
          err?.message ||
          "No se pudieron cargar los mensajes.";
        setError(typeof message === "string" ? message : "No se pudieron cargar los mensajes.");
        setMessages([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingMessages(false);
      });

    clearUnread(selectedId);
    markConversationRead(selectedId)
      .then(() => refreshConversations(true))
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId || !token) return;
    const stream = openMessagesStream(selectedId, token);

    stream.addEventListener("message:new", (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data) as ChatMessage;
        setMessages((current) => {
          if (current.some((message) => message.id === payload.id)) return current;
          return [...current, payload];
        });
        const incomingIsForMe =
          mode === "coach"
            ? payload.senderRole === "CLIENT"
            : payload.senderRole === "COACH" || payload.senderRole === "ADMIN";
        if (incomingIsForMe) {
          clearUnread(selectedId);
          markConversationRead(selectedId)
            .then(() => refreshConversations(true))
            .catch(() => undefined);
        } else {
          refreshConversations(true);
        }
      } catch {
        // ignore malformed payload
      }
    });

    stream.addEventListener("message:read", () => {
      refreshConversations(true);
    });

    stream.onerror = () => {
      stream.close();
    };

    return () => {
      stream.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, token, mode]);

  useEffect(() => {
    const body = chatBodyRef.current;
    if (!body) return;
    body.scrollTop = body.scrollHeight;
  }, [selectedId, messages.length]);

  useEffect(() => {
    if (!emojiOpen) return;

    function handlePointerDown(event: PointerEvent) {
      const target = event.target as Node | null;
      if (target && emojiWrapRef.current?.contains(target)) return;
      setEmojiOpen(false);
    }

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [emojiOpen]);

  const visibleConversations = useMemo(() => {
    const query = search.trim().toLowerCase();
    return conversations.filter((conv) => {
      const matchesSearch =
        !query ||
        conv.title.toLowerCase().includes(query) ||
        (conv.lastMessage?.content.toLowerCase().includes(query) ?? false);
      const matchesFilter = filter === "Todos" || (filter === "No leidos" && conv.unread > 0);
      return matchesSearch && matchesFilter;
    });
  }, [conversations, filter, search]);

  const unreadTotal = conversations.reduce((total, conv) => total + conv.unread, 0);

  function clearUnread(conversationId: string) {
    setConversations((current) =>
      current.map((conversation) =>
        conversation.id === conversationId ? { ...conversation, unread: 0 } : conversation,
      ),
    );
  }

  function selectConversation(conversationId: string) {
    setSelectedId(conversationId);
    clearUnread(conversationId);
    setMobileView("chat");
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!selected || !draft.trim() || sending) return;
    setSending(true);
    try {
      const message = await sendMessageTo(selected.clientId, draft.trim());
      setMessages((current) => {
        if (current.some((existing) => existing.id === message.id)) return current;
        return [...current, message];
      });
      setDraft("");
      refreshConversations(true);
    } catch (err: any) {
      const messageText =
        err?.response?.data?.message ||
        err?.message ||
        "No se pudo enviar el mensaje.";
      setError(typeof messageText === "string" ? messageText : "No se pudo enviar el mensaje.");
    } finally {
      setSending(false);
    }
  }

  function addEmoji(emoji: string) {
    setDraft((value) => `${value}${emoji}`);
    setEmojiOpen(false);
  }

  const activeName = selected?.title ?? (mode === "coach" ? "Selecciona un cliente" : "Coach Principal");
  const activeInitials = initials(activeName);
  const latestReceiverPhoto = [...messages]
    .reverse()
    .find((message) => !mineFor(mode, message) && message.senderProfilePhotoUrl)?.senderProfilePhotoUrl;
  const activePhotoSrc = resolveProfilePhotoSrc(selected?.profilePhotoUrl ?? latestReceiverPhoto);
  const myName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.email?.split("@")[0] || "Yo";
  const myInitials = initials(myName);
  const myPhotoSrc = resolveProfilePhotoSrc(user?.profile_photo_url);
  const sideTitle = activeName;
  const sideStatus = selected?.hasPortalAccount === false ? "Sin cuenta de portal" : mode === "coach" ? "Cliente activo" : "En línea";

  return (
    <div className={`messages-page messages-page--whatsapp messages-page--${mode} messages-page--mobile-${mobileView}`}>
      <section className="messages-layout messages-layout--whatsapp">
        <aside className="messages-inbox">
          <header className="messages-column-head">
            <div>
              <p>{mode === "coach" ? "Portal coach" : "Portal cliente"}</p>
              <h1>Chats</h1>
            </div>
            {unreadTotal ? <span>{unreadTotal}</span> : null}
          </header>
          <div className="messages-search-row">
            <label>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={mode === "coach" ? "Buscar cliente..." : "Buscar mensaje..."}
              />
            </label>
          </div>
          <div className="messages-filter-row">
            {(["Todos", "No leidos"] as FilterKey[]).map((item) => (
              <button key={item} type="button" className={filter === item ? "active" : ""} onClick={() => setFilter(item)}>
                {item}
              </button>
            ))}
          </div>
          <div className="messages-thread-list">
            {loadingThreads ? (
              <div className="messages-empty-state">
                <strong>Cargando conversaciones...</strong>
              </div>
            ) : error && !conversations.length ? (
              <div className="messages-empty-state">
                <strong>No se pudo cargar la mensajeria</strong>
                <p>{error}</p>
                <p style={{ marginTop: 8, fontSize: 12, opacity: 0.7 }}>
                  Verifica que el backend este corriendo y que hayas aplicado la migración de la base de datos.
                </p>
              </div>
            ) : visibleConversations.length ? (
              visibleConversations.map((conversation, index) => {
                const conversationPhotoSrc = resolveProfilePhotoSrc(conversation.profilePhotoUrl);

                return (
                  <article
                    key={conversation.id}
                    className={`messages-thread ${conversation.id === selectedId ? "active" : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => selectConversation(conversation.id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") selectConversation(conversation.id);
                    }}
                  >
                    <span
                      className={`messages-thread-icon${conversationPhotoSrc ? " messages-thread-icon--has-photo" : ` messages-thread-icon--${index % 3 === 0 ? "photo" : "chart"}`}`}
                      aria-hidden="true"
                    >
                      {conversationPhotoSrc ? (
                        <img src={conversationPhotoSrc} alt={conversation.title} />
                      ) : (
                        initials(conversation.title)
                      )}
                    </span>
                    <div>
                      <strong>{conversation.title}</strong>
                      <p>{conversation.subtitle}</p>
                    </div>
                    <aside>
                      <small>{conversation.lastMessage ? formatTime(conversation.lastMessage.createdAt) : ""}</small>
                      {conversation.unread ? <b>{conversation.unread}</b> : null}
                    </aside>
                  </article>
                );
              })
            ) : (
              <div className="messages-empty-state">
                <strong>{mode === "coach" ? "Sin clientes con cuenta" : "Aún no hay conversación"}</strong>
                <p>
                  {mode === "coach"
                    ? "Tus clientes apareceran aquí cuando tengan una cuenta de portal."
                    : "Cuando tu coach te escriba verás los mensajes aquí."}
                </p>
              </div>
            )}
          </div>
        </aside>

        <article className="messages-chat">
          <header className="messages-chat-head">
            <button
              type="button"
              className="messages-chat-back"
              aria-label="Volver a la lista de chats"
              onClick={() => setMobileView("list")}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <div className={`messages-coach-avatar${activePhotoSrc ? " messages-coach-avatar--photo" : ""}`}>
              <span>
                {activePhotoSrc ? (
                  <img src={activePhotoSrc} alt={activeName} />
                ) : (
                  activeInitials
                )}
              </span>
              <i />
            </div>
            <div>
              <h2>{sideTitle}</h2>
              <p>{sideStatus}</p>
            </div>
          </header>

          {error ? (
            <div className="messages-call-banner" role="alert">
              <strong>Mensajeria</strong>
              <p>{error}</p>
              <button type="button" onClick={() => setError(null)}>Cerrar</button>
            </div>
          ) : null}

          <div className="messages-chat-body" ref={chatBodyRef}>
            {loadingMessages ? (
              <div className="messages-empty-state messages-empty-state--chat">
                <strong>Cargando mensajes...</strong>
              </div>
            ) : selected && messages.length ? (
              messages.map((message) => {
                const mine = mineFor(mode, message);
                const otherPhotoSrc = resolveProfilePhotoSrc(message.senderProfilePhotoUrl) ?? activePhotoSrc;
                const ownPhotoSrc = resolveProfilePhotoSrc(message.senderProfilePhotoUrl) ?? myPhotoSrc;
                const seen = mine
                  ? mode === "coach"
                    ? message.readByClient
                    : message.readByCoach
                  : false;
                return (
                  <div key={message.id} className={`messages-chat-row messages-chat-row--${mine ? "me" : "coach"}`}>
                    {!mine ? (
                      <span
                        className={`messages-chat-avatar messages-chat-avatar--other${otherPhotoSrc ? " messages-chat-avatar--photo" : ""}`}
                        aria-hidden="true"
                      >
                        {otherPhotoSrc ? (
                          <img src={otherPhotoSrc} alt={activeName} />
                        ) : (
                          initials(activeName)
                        )}
                      </span>
                    ) : null}
                    <div className="messages-bubble">
                      {!mine ? <span className="messages-bubble-name">{activeName}</span> : null}
                      <p>{message.content}</p>
                      <span className="messages-bubble-meta">
                        <span className="messages-bubble-time">{formatTime(message.createdAt)}</span>
                        {mine ? (
                          <span className={`messages-bubble-ticks ${seen ? "messages-bubble-ticks--seen" : ""}`} aria-label={seen ? "visto" : "enviado"}>
                            <svg viewBox="0 0 18 12" aria-hidden="true">
                              <path d="M1 7.2 4.4 10.6 11.6 3.4" />
                              <path d="M6.4 7.2 9.8 10.6 17 3.4" />
                            </svg>
                          </span>
                        ) : null}
                      </span>
                    </div>
                    {mine ? (
                      <span
                        className={`messages-chat-avatar messages-chat-avatar--me${ownPhotoSrc ? " messages-chat-avatar--photo" : ""}`}
                        aria-hidden="true"
                      >
                        {ownPhotoSrc ? (
                          <img src={ownPhotoSrc} alt={myName} />
                        ) : (
                          myInitials
                        )}
                      </span>
                    ) : null}
                  </div>
                );
              })
            ) : selected ? (
              <div className="messages-empty-state messages-empty-state--chat">
                <strong>No hay mensajes todavia</strong>
                <p>Escribe el primer mensaje para iniciar la conversación.</p>
              </div>
            ) : (
              <div className="messages-empty-state messages-empty-state--chat">
                <strong>Selecciona una conversación</strong>
                <p>Elige un cliente del panel izquierdo para chatear.</p>
              </div>
            )}
          </div>

          <form className="messages-composer" onSubmit={handleSubmit}>
            <div className="messages-input-wrap">
            <input
              value={draft}
              disabled={!selected || sending || selected.hasPortalAccount === false}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={
                !selected
                  ? "Selecciona una conversación"
                  : selected.hasPortalAccount === false
                    ? "Cliente sin cuenta de portal"
                    : mode === "coach"
                      ? "Escribe un mensaje para el cliente..."
                      : "Escribe un mensaje para tu coach..."
              }
            />
            <div className="messages-emoji-wrap" ref={emojiWrapRef}>
              <button
                type="button"
                aria-label="Emoji"
                disabled={!selected}
                onClick={() => setEmojiOpen((value) => !value)}
              >
                :)
              </button>
              {emojiOpen ? (
                <div className="messages-emoji-picker" role="menu" aria-label="Seleccionar emoji">
                  {EMOJIS.map((emoji) => (
                    <button key={emoji} type="button" onClick={() => addEmoji(emoji)}>
                      <span className="messages-emoji-glyph">{emoji}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            </div>
            <button
              type="submit"
              className="messages-send"
              disabled={!selected || !draft.trim() || sending || selected.hasPortalAccount === false}
            >
              <span>{sending ? "Enviando..." : "Enviar"}</span>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22 2 11 13" />
                <path d="m22 2-7 20-4-9-9-4 20-7Z" />
              </svg>
            </button>
          </form>
        </article>
      </section>
    </div>
  );
}
